/**
 * 패널 플래닝 로딩·지도 성능 디버그 로그.
 * DevTools 콘솔 필터: panel-perf
 * 프로덕션에서도 켜려면: window.__PANEL_PERF__ = true
 */

import { isAppDebugEnabled } from '@/utils/debug/debugEnv';

const NS = '[panel-perf]';

export function panelPerfEnabled() {
  if (typeof window !== 'undefined') {
    if (window.__PANEL_PERF__ === true || window.__APP_PERF__ === true) {
      return true;
    }
  }
  return isAppDebugEnabled();
}

const marks = new Map();

/** @type {Record<string, { start: number, end: number, error: number, inFlight: number }>} */
const tileStatsByLayer = {};

export function panelPerfMark(label, detail) {
  if (!panelPerfEnabled()) return;
  const t = performance.now();
  marks.set(label, t);
  if (detail !== undefined) {
    console.log(NS, label, detail, `@${t.toFixed(1)}ms`);
  } else {
    console.log(NS, label, `@${t.toFixed(1)}ms`);
  }
}

export function panelPerfMeasure(label, sinceLabel, detail) {
  if (!panelPerfEnabled()) return undefined;
  const end = performance.now();
  const start = sinceLabel ? marks.get(sinceLabel) : undefined;
  const dur = start != null ? end - start : undefined;
  const durText = dur != null ? `${dur.toFixed(1)}ms` : '(no mark)';
  if (detail !== undefined) {
    console.log(NS, label, durText, detail);
  } else {
    console.log(NS, label, durText);
  }
  return dur;
}

export async function panelPerfAsync(label, fn) {
  if (!panelPerfEnabled()) {
    return fn();
  }
  const start = performance.now();
  panelPerfMark(`${label}:start`);
  try {
    const result = await fn();
    console.log(NS, `${label}:done`, `${(performance.now() - start).toFixed(1)}ms`);
    return result;
  } catch (error) {
    console.log(
      NS,
      `${label}:error`,
      `${(performance.now() - start).toFixed(1)}ms`,
      error?.message || error
    );
    throw error;
  }
}

export function panelPerfSync(label, fn) {
  if (!panelPerfEnabled()) {
    return fn();
  }
  const start = performance.now();
  panelPerfMark(`${label}:start`);
  try {
    const result = fn();
    console.log(NS, `${label}:done`, `${(performance.now() - start).toFixed(1)}ms`);
    return result;
  } catch (error) {
    console.log(
      NS,
      `${label}:error`,
      `${(performance.now() - start).toFixed(1)}ms`,
      error?.message || error
    );
    throw error;
  }
}

export function panelPerfCountSummary(label, counts) {
  if (!panelPerfEnabled()) return;
  console.log(NS, label, counts);
}

export function panelPerfResetTileStats() {
  Object.keys(tileStatsByLayer).forEach(key => {
    tileStatsByLayer[key] = { start: 0, end: 0, error: 0, inFlight: 0 };
  });
}

function ensureTileStats(layerKey) {
  if (!tileStatsByLayer[layerKey]) {
    tileStatsByLayer[layerKey] = { start: 0, end: 0, error: 0, inFlight: 0 };
  }
  return tileStatsByLayer[layerKey];
}

export function panelPerfAttachTileLayerWatch(tileLayer, layerKey) {
  if (!panelPerfEnabled() || !tileLayer) return;

  const source = tileLayer.getSource?.();
  if (!source?.on) return;

  panelPerfMark(`tile-watch:${layerKey}`, 'WMS tile source listener attached');

  source.on('tileloadstart', () => {
    const stats = ensureTileStats(layerKey);
    stats.start += 1;
    stats.inFlight += 1;
    if (stats.start <= 3 || stats.start % 10 === 0) {
      console.log(NS, 'tile:start', layerKey, {
        totalStarted: stats.start,
        inFlight: stats.inFlight
      });
    }
  });

  source.on('tileloadend', () => {
    const stats = ensureTileStats(layerKey);
    stats.end += 1;
    stats.inFlight = Math.max(0, stats.inFlight - 1);
  });

  source.on('tileloaderror', () => {
    const stats = ensureTileStats(layerKey);
    stats.error += 1;
    stats.inFlight = Math.max(0, stats.inFlight - 1);
    console.warn(NS, 'tile:error', layerKey, { ...stats });
  });
}

export function panelPerfLogTileStats(reason) {
  if (!panelPerfEnabled()) return;
  const snapshot = Object.fromEntries(
    Object.entries(tileStatsByLayer).map(([key, stats]) => [key, { ...stats }])
  );
  console.log(NS, 'tile:summary', reason || 'snapshot', snapshot, `@${performance.now().toFixed(1)}ms`);
}

export function panelPerfAttachMapRenderWatch(map, label = 'map') {
  if (!panelPerfEnabled() || !map?.on) return;

  let renderCompleteTimer = null;
  map.on('movestart', () => {
    panelPerfMark(`${label}:movestart`);
    panelPerfLogTileStats(`${label} movestart (before)`);
  });
  map.on('moveend', () => {
    panelPerfMeasure(`${label}:moveend`, `${label}:movestart`);
    panelPerfLogTileStats(`${label} moveend`);
  });
  map.on('rendercomplete', () => {
    if (renderCompleteTimer) clearTimeout(renderCompleteTimer);
    renderCompleteTimer = setTimeout(() => {
      panelPerfLogTileStats(`${label} rendercomplete`);
    }, 200);
  });
}
